import { useLazyQuery } from '@apollo/client';
import { GET_CHANNEL_IDEA, Idea } from '@frontend/data-access';
import { Button, Empty, Skeleton } from 'antd';
import { useRouter } from 'next/router';
import QueueAnim from 'rc-queue-anim';
import { useEffect } from 'react';
import IdeaEvaluate from '../idea-evaluate/idea-evaluate';
import IdeaEvaluationDetail from './detail';
import useTranslation from 'next-translate/useTranslation';

export function EvaluateIdeasPending({ ideaIds }: { ideaIds: string[] }) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const ideaId = router.query['ideaId'] as string;
  const [getData, { data, loading }] = useLazyQuery<{ channelIdea: Idea }>(
    GET_CHANNEL_IDEA
  );

  const handleClickBack = () => {
    if (ideaId) {
      const index = ideaIds.indexOf(ideaId);
      if (index > 0) {
        const newId = ideaIds[index - 1];
        router.push({ query: { ideaId: newId } });
      }
    }
  };

  const handleClickNext = () => {
    if (ideaId) {
      const index = ideaIds.indexOf(ideaId);
      if (index < ideaIds.length - 1) {
        const newId = ideaIds[index + 1];
        router.push({ query: { ideaId: newId } });
      }
    }
  };

  useEffect(() => {
    if (ideaIds.length && !ideaId) {
      const initialId = ideaIds[0];
      router.push({ query: { ideaId: initialId } });
    }
  }, [ideaId, ideaIds, router]);

  useEffect(() => {
    if (ideaId) {
      getData({ variables: { ideaId } });
    }
  }, [ideaId, getData]);

  if (loading)
    return (
      <div className="bg-gray-100 rounded-lg p-6 grid md:grid-cols-3 gap-6 h-72">
        <div className="col-span-2">
          <Skeleton active />
        </div>
        <div className="col-span-1">
          <Skeleton active />
        </div>
      </div>
    );

  if (ideaId && data?.channelIdea)
    return (
      <div className="space-y-5">
        <QueueAnim>
          <div
            key="idea"
            className={`bg-gray-100 border border-gray-400 rounded-lg p-3 transition-opacity duration-200 opacity-100`}
          >
            <div className="grid grid-cols-6 lg:divide-x-2 gap-5">
              <div className="col-span-12 lg:col-span-4">
                <IdeaEvaluationDetail idea={data.channelIdea} />
              </div>
              <div className="col-span-12 lg:col-span-2 lg:pl-5">
                <IdeaEvaluate ideaId={data.channelIdea.id} />
              </div>
            </div>
          </div>
        </QueueAnim>
        <div className="flex items-center justify-end gap-4 mr-16">
          <Button
            ghost
            type="primary"
            shape="round"
            onClick={handleClickBack}
            disabled={ideaIds.indexOf(ideaId) === 0}
          >
            {t('back')}
          </Button>
          <div>{`${ideaIds.indexOf(ideaId) + 1} / ${ideaIds.length}`}</div>
          <Button
            type="primary"
            shape="round"
            onClick={handleClickNext}
            disabled={ideaIds.indexOf(ideaId) === ideaIds.length - 1}
          >
            {t('next')}
          </Button>
        </div>
      </div>
    );

  return (
    <div className="p-5 text-center">
      <Empty description={t('no_pending_ideas')} />
    </div>
  );
}

export default EvaluateIdeasPending;
